/* globals AbortController */
import lscache from 'lscache'

class BaseService {
  constructor (options = {}) {
    this.logger = options.logger || console
    this.cache = options.cache || lscache
    this.queryParams = options.queryParams
    this.resourceId = options.resourceId
    this.method = 'GET'
    this.body = null
  }

  async fetch () {
    this.abortController = new AbortController()
    let results
    try {
      this.debug('Attempting to fetch from cache...')
      results = await this.fromCache()
      if (results) {
        this.debug('Cache hit...', results)
        return results
      } else this.debug('Cache miss...')
    } catch (err) {
      this.debug('Encountered an error attempting to fetch from the cache... continuing anyways', err)
    }

    try {
      this.debug('Attempting to fetch from API...')
      results = await this.fromApi()
      this.toCache(results)
      return results
    } catch (err) {
      if (err.name === 'AbortError') {
        this.debug('Request to API was aborted...')
      } else {
        this.debug('Encountered an error attempting to fetch from the API...', err)
        throw err
      }
    }
  }

  async fromApi () {
    return window.fetch(this.apiUrlWithQueryParams.href, {
      method: this.method,
      headers: this.fetchHeaders,
      body: this.body,
      signal: this.abortController.signal
    }).then(response => {
      if (response.ok) return response.json()
      // We got a bad response.
      // Going to bail but first we'll try
      // to get the text from the response to log
      return response.text().then(text => {
        throw new Error(`API failed (${response.status}): ${text}`)
      })
    }).then(data => {
      this.debug('Received the following from the API...', data)
      return this.convertFromApi(data)
    })
  }

  fromCache () {
    return Promise.resolve(this.cache.get(this.cacheKey))
  }

  toCache (results) {
    try {
      this.cache.set(this.cacheKey, results, this.cacheExpiration)
    } catch (err) {
      this.debug('Encounted an error attempting to store the results in the cache...', err)
    }
  }

  async convertFromApi (data) {
    return Promise.resolve(data)
  }

  abort () {
    this.abortController && this.abortController.abort()
  }

  debug (...args) {
    this.log('debug', ...args)
  }

  info (...args) {
    this.log('info', ...args)
  }

  warn (...args) {
    this.log('warn', ...args)
  }

  error (...args) {
    this.log('error', ...args)
  }

  log (which, ...args) {
    // TODO: consider turning off logging in production
    //       maybe include the ability to turn it on via console though
    if (this.logger && which in this.logger) {
      this.logger[which](this.loggerTag, ...args)
    }
  }

  get serviceName () {
    return 'base'
  }

  get fetchHeaders () {
    return {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'Api-Site': window.location.host
    }
  }

  get baseApiUrl () {
    return new URL('/janiis/v1/', window.location)
  }

  get apiUrl () {
    let uri = `${this.serviceName}/`
    if (this.resourceId) uri = `${uri}${this.resourceId}/`
    return new URL(uri, this.baseApiUrl)
  }

  get apiUrlWithQueryParams () {
    const url = this.apiUrl
    for (const key of this.searchParams.keys()) {
      url.searchParams.append(key, this.searchParams.get(key))
    }
    return url
  }

  get cacheExpiration () {
    // TODO: this should probably be configurable
    //       or at least changeable for dev versus production
    return 10 // seconds
  }

  get cacheVersion () {
    return 'v1'
  }

  get cacheKey () {
    return ['services', this.serviceName, this.resourceId, this.searchParams.toString(), this.cacheKeyExtras, this.cacheVersion].filter(part => part).join(':')
  }

  get cacheKeyExtras () {
    return ''
  }

  get loggerTag () {
    return this.loggerTags.reduce((tags, tag) => {
      if (tag && tag !== 'undefined') return `${tags}[${tag}]`
      else return tag
    }, '')
  }

  get loggerTags () {
    const tags = ['services', this.serviceName]
    if (this.resourceId) tags.push(this.resourceId)
    const searchParams = this.searchParams.toString()
    if (searchParams) tags.push(searchParams)
    return tags
  }

  set queryParams (value = {}) {
    this.__queryParams = value
    this.__searchParams = new URLSearchParams()
    Object.keys(value).forEach(key => this.__searchParams.append(key, value[key]))
  }

  get queryParams () {
    return this.__queryParams
  }

  get searchParams () {
    return this.__searchParams
  }
}

export default BaseService
